<h4>What this screen is for</h4>
<p>
    Pricing a group booking. Given a date window, a journey, a class and a group
    size, it lists the services that could carry the group and shows, for each, the
    cheapest bucket that still covers the revenue displaced by taking those seats.
</p>

<h4>Getting a quote</h4>
<p>
    All five filters are required: <strong>Class</strong>, the
    <strong>date range</strong>, the <strong>journey</strong> (one origin and
    destination only on this screen), the <strong>time window</strong> and the
    <strong>group size</strong>. Search returns one row per candidate service.
</p>

<h4>Return searches</h4>
<p>
    Choose <strong>Round-trip</strong> to price the journey back in the same search. By
    default the return runs the other way along your route; you can choose a different
    <strong>Return origin</strong> station, and the return still ends at your outbound
    origin. The return has its own dates and time window; class,
    group size and days of the week are shared. Results appear as separate
    <strong>Outbound</strong> and <strong>Return</strong> lists, each priced on its own, and
    Export puts both in one file with a Direction column.
</p>
<p>
    Dates in the past can't be searched, the From time must not be later than the To time,
    and the group size must be at least 1.
</p>

<h4>Reading the results</h4>
<ul>
    <li><strong>Constrained Leg</strong> &mdash; the leg that limits the sale, and where the displacement cost is calculated.</li>
    <li><strong>Lead Bucket</strong> and <strong>Lead Bucket Fare</strong> &mdash; what the service is currently selling at.</li>
    <li><strong>Rec Bucket</strong> &mdash; the bucket the system recommends for the group.</li>
    <li><strong>Bucket</strong> &mdash; editable. Pick a different bucket and the fare and total quote are recalculated.</li>
    <li><strong>Displacement Dmd</strong> &mdash; the number of seats being displaced, which is the group size you entered.</li>
    <li><strong>Total Quote</strong> &mdash; the fare for the chosen bucket multiplied by the group size, after any discount.</li>
</ul>
<p>
    Selecting <strong>Unavailable</strong> in the Bucket column clears the quote on
    screen. It is a display state only and is not saved anywhere.
</p>

<h4>Row actions</h4>
<ul>
    <li>The <strong>comment</strong> icon opens notes against that quote.</li>
    <li>
        The <strong>edit</strong> icon opens the same override window used on the
        Inventory workbench, so you can adjust the bucket allocations directly before
        quoting. Saving there now asks whether the override should reach the reservation
        system straight away or wait for the next scheduled export.
    </li>
    <li>
        The <strong>save</strong> icon applies the group sale. You will be asked to
        confirm, and on confirmation the allocations are written and the sale is sent
        onward to the reservation system.
    </li>
</ul>

<h4>Worth knowing</h4>
<ul>
    <li>
        <strong>Applying a group sale changes inventory and cannot be undone from this
        screen.</strong> It raises the allocation on the chosen bucket and every bucket
        above it to cover the group, then sends the result on.
    </li>
    <li>
        <strong>Services the group cannot be sold on are left out of the results
        entirely</strong> rather than shown as unavailable, so a short list may mean
        "few options" rather than "few trains".
    </li>
    <li>
        The <strong>Discount</strong> column only appears where discounting is enabled
        for your operator.
    </li>
    <li>
        The Excel export labels the fare column differently from the grid and leaves out
        the discount.
    </li>
</ul>
